import type { IndexDateCriterion } from '@verantos-dev/types-cohorts';

import type { AnalyticalVariableWithChangeStatus, CohortDefinitionStoreState } from './types';

// ---------------------------------------------------------------------------
// AV selectors
// ---------------------------------------------------------------------------

export const selectAVById =
  (id: string | undefined) =>
  (state: CohortDefinitionStoreState): AnalyticalVariableWithChangeStatus | undefined =>
    id ? state.analyticalVariables.find((av) => av.id === id) : undefined;

/** Select the sourceLibraryAvId for a specific AV criterion (returns a primitive for stable subscriptions). */
export const selectAvCriterionSourceLibraryAvId =
  (avId: string) =>
  (state: CohortDefinitionStoreState): string | null | undefined => {
    const c = state.criteria.find(
      (c) =>
        'criteriaType' in c &&
        c.criteriaType === 'analytical_variable' &&
        'analyticalVariableId' in c &&
        (c as { analyticalVariableId: string }).analyticalVariableId === avId
    );
    return c && 'sourceLibraryAvId' in c
      ? (c as { sourceLibraryAvId: string | null | undefined }).sourceLibraryAvId
      : undefined;
  };

/** Select the criteriaSelection string for a specific AV criterion (returns a primitive for stable subscriptions). */
export const selectCriteriaSelectionByAVId =
  (avId: string) =>
  (state: CohortDefinitionStoreState): string | undefined => {
    const c = state.criteria.find(
      (c) =>
        'criteriaType' in c &&
        c.criteriaType === 'analytical_variable' &&
        'analyticalVariableId' in c &&
        (c as { analyticalVariableId: string }).analyticalVariableId === avId
    );
    return c && 'criteriaSelection' in c
      ? (c as { criteriaSelection: string }).criteriaSelection
      : undefined;
  };

/** Type guard to narrow a Criterion to an IndexDateCriterion. */
const isIndexDateCriterion = (
  c: CohortDefinitionStoreState['criteria'][number]
): c is IndexDateCriterion => 'criteriaType' in c && c.criteriaType === 'index_date';

/** Check if an AV is used as the index date criterion (returns a primitive for stable subscriptions). */
export const selectIsIndexDateAV =
  (avId: string) =>
  (state: CohortDefinitionStoreState): boolean =>
    state.criteria.some(
      (c) =>
        isIndexDateCriterion(c) &&
        c.value.type === 'Analytical variable' &&
        c.value.analyticalVariableId === avId
    );

export const selectAnalyticalVariables = (state: CohortDefinitionStoreState) =>
  state.analyticalVariables;

export const selectCriteria = (state: CohortDefinitionStoreState) => state.criteria;

/**
 * True when the cohort still has at least one *unresolved stub* — a criterion
 * the drafter planned but that isn't yet bound to an analytical variable
 * (`analyticalVariableId` AND `sourceLibraryAvId` both empty). Calendar-date
 * index criteria are already resolved and never count.
 *
 * This is the cohort-state half of the single-writer "agent owns the cohort"
 * lock (the cohort is mid-assembly whenever a stub exists). Computed from the
 * raw criteria array — deliberately NOT from the card's `isStub`, which also
 * trips when the analytical-variable library list simply hasn't loaded yet.
 */
export const selectHasUnresolvedStubs = (state: CohortDefinitionStoreState): boolean =>
  state.criteria.some((c) => {
    if (c.criteriaType === 'analytical_variable') {
      return c.analyticalVariableId == null && !c.sourceLibraryAvId;
    }
    if (c.criteriaType === 'index_date' && c.value.type === 'Analytical variable') {
      return c.value.analyticalVariableId == null && !c.value.sourceLibraryAvId;
    }
    return false;
  });

export const selectValidationNonce =
  (avId: string) =>
  (state: CohortDefinitionStoreState): number =>
    state.validationRequests[avId] ?? 0;

export const selectHasConceptGroupErrors =
  (avId: string) =>
  (state: CohortDefinitionStoreState): boolean =>
    (state.conceptGroupErrorCounts[avId] ?? 0) > 0;

export const selectRecentlyAppliedForAV = (avId: string) => (state: CohortDefinitionStoreState) =>
  state.recentlyAppliedConceptGroups.filter((entry) => entry.avId === avId);

export const selectPendingPreviewAvIds = (state: CohortDefinitionStoreState) =>
  state.pendingPreviewAvIds;

// ---------------------------------------------------------------------------
// Study period selectors
// ---------------------------------------------------------------------------

export const selectStudyPeriodStartDate = (state: CohortDefinitionStoreState) =>
  state.studyPeriod.overallStudyPeriodRestriction?.startDate;

export const selectStudyPeriodEndDate = (state: CohortDefinitionStoreState) =>
  state.studyPeriod.overallStudyPeriodRestriction?.endDate;

export const selectStudyPeriodChangeStatus = (state: CohortDefinitionStoreState) =>
  state.studyPeriod.changeStatus;

export const selectStudyPeriodChangedBy = (state: CohortDefinitionStoreState) =>
  state.studyPeriod.changedBy;

// ---------------------------------------------------------------------------
// Profile selectors
// ---------------------------------------------------------------------------

export const selectLatestStatus = (state: CohortDefinitionStoreState) => state.profile.latestStatus;

export const selectLatestCohortVersionId = (state: CohortDefinitionStoreState) =>
  state.profile.latestCohortVersionId;

/**
 * Effective edit permission, gated on the `asset_edit_while_in_use` flag:
 * - flag ON: cohort owner. A cohort is editable regardless of analytical-dataset
 *   (AD) usage — only *reprocessing* is blocked, and only while a dependent AD is
 *   actively PROCESSING (see `useCohortDetailActions`/`useCohortBuilderPageActions`
 *   for the Process button's own disable logic).
 * - flag OFF (legacy): cohort owner AND not locked by AD usage. Folding the AD
 *   lock in here is the single chokepoint — every `viewMode`/`isEditable`
 *   consumer (sections, Save/Process/Revert) goes read-only without per-component
 *   changes.
 *
 * `assetEditWhileInUseEnabled` is hydrated from the flag by the profile/
 * hydration hooks so this pure selector can gate without reading the flag.
 */
// TODO: cleanup with asset_edit_while_in_use ff
export const selectIsEditable = (state: CohortDefinitionStoreState) =>
  state.profile.isEditable &&
  (state.assetEditWhileInUseEnabled || (state.profile.usedInAnalyticalDatasets?.length ?? 0) === 0);

/** Raw ownership flag, independent of the analytical-dataset lock. */
export const selectIsOwner = (state: CohortDefinitionStoreState) => state.profile.isEditable;

export const selectUsedInAnalyticalDatasets = (state: CohortDefinitionStoreState) =>
  state.profile.usedInAnalyticalDatasets ?? [];

export const selectIsSaving = (state: CohortDefinitionStoreState) => state.profile.isSaving;

export const selectIsExecutionPending = (state: CohortDefinitionStoreState) =>
  state.profile.isExecutionPending;

export const selectHasStagedChanges = (state: CohortDefinitionStoreState) => state.hasStagedChanges;

export const selectLatestSubjectCount = (state: CohortDefinitionStoreState) =>
  state.profile.latestSubjectCount;

export const selectLatestDatasetReleaseId = (state: CohortDefinitionStoreState) =>
  state.profile.latestDatasetReleaseId;

export const selectLatestDatasetReleaseDisplayName = (state: CohortDefinitionStoreState) =>
  state.profile.latestDatasetReleaseDisplayName;

// ---------------------------------------------------------------------------
// Legacy entity selectors
// ---------------------------------------------------------------------------

export const selectSimpleEvents = (state: CohortDefinitionStoreState) => state.simpleEvents;

export const selectDerivedEvents = (state: CohortDefinitionStoreState) => state.derivedEvents;

export const selectIndexDate = (state: CohortDefinitionStoreState) => state.indexDate;

export const selectSubjectVariables = (state: CohortDefinitionStoreState) => state.subjectVariables;

// ---------------------------------------------------------------------------
// Concept group selectors
// ---------------------------------------------------------------------------

export const selectConceptGroups = (state: CohortDefinitionStoreState) => state.conceptGroups;

export const selectConceptGroupSelections = (state: CohortDefinitionStoreState) =>
  state.conceptGroupSelections;

export const selectConceptGroupsConfidence = (state: CohortDefinitionStoreState) =>
  state.conceptGroupsConfidence;

export const selectConceptGroupSelectionsWithIssues = (state: CohortDefinitionStoreState) =>
  state.conceptGroupSelections.filter(
    (s) => (s.status === 'warning' || s.status === 'error') && !s.reported
  );

// ---------------------------------------------------------------------------
// Modal + active item selectors
// ---------------------------------------------------------------------------

export const selectModal = (state: CohortDefinitionStoreState) => state.modal;

export const selectActiveCohortBuilderItem = (state: CohortDefinitionStoreState) =>
  state.activeCohortBuilderItem;

export const selectCohortBuilderItemViewed =
  (id: string) =>
  (state: CohortDefinitionStoreState): boolean =>
    state.viewedCohortBuilderItems[id] ?? false;
